/**
 * Comments API - Update Operations
 *
 * Updating existing comments.
 */

import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../../firebaseConfig";
import { ensureFreshToken } from "../authUtils";

/**
 * Updates the text of an existing comment.
 * Only `text` field is written (legacy `content` field deprecated).
 *
 * @param {string} commentId
 * @param {{ text: string, username?: string }} commentData
 * @returns {Promise<true>}
 */
export const updateComment = async (commentId, commentData) => {
  try {
    await ensureFreshToken();
    const currentUid = auth?.currentUser?.uid || null;
    if (!currentUid) {
      throw new Error("You must be logged in to update a comment");
    }

    // ── Phase 1: Data Sanitization (Source of Truth) ──
    const trimmedText = (commentData.text || "").trim();

    if (!trimmedText) {
      throw new Error("Comment text cannot be empty");
    }
    if (trimmedText.length > 2000) {
      throw new Error(
        `Comment exceeds maximum length of 2000 characters (got ${trimmedText.length})`,
      );
    }

    const commentRef = doc(db, "comments", commentId);

    // Client-side ownership check before writing
    const commentSnap = await getDoc(commentRef);
    if (!commentSnap.exists()) {
      throw new Error("Comment does not exist");
    }
    const existingData = commentSnap.data();
    if (existingData.userId) {
      if (existingData.userId !== currentUid) {
        throw new Error("You do not have permission to update this comment");
      }
    } else {
      // Legacy username fallback
      const commentUsername = (existingData.username || "").trim().toLowerCase();
      const clientUsername = (commentData.username || "").trim().toLowerCase();
      if (!commentUsername || commentUsername !== clientUsername) {
        throw new Error("You do not have permission to update this comment");
      }
    }

    // Issue 4 Fix: Remove 'content' field - Firestore security rules only allow updating ['text', 'updatedAt', 'deletedAt', 'mentions', 'mentionUserIds']
    await updateDoc(commentRef, {
      text: trimmedText,
      updatedAt: serverTimestamp(),
    });

    return true;
  } catch (error) {
    console.error("Error updating comment:", error);
    throw error;
  }
};
